import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";
import { disputesAPI, paymentsAPI } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { ScrollArea } from "../components/ui/scroll-area";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { 
    Shield, 
    ArrowLeft,
    CheckCircle2,
    Clock,
    AlertTriangle,
    XCircle,
    ArrowRight,
    RefreshCw,
    MessageSquare,
    FileText,
    Lock,
    CreditCard,
    ChevronRight,
    History,
    Sparkles,
    Scale,
    Send,
    Flag,
    Copy,
    Download
} from "lucide-react";
import { toast } from "sonner";
import PricingModal from "../components/PricingModal";

const STATUSES = {
    pending: { label: "Not Started", color: "bg-gray-100 text-gray-700", icon: Clock },
    letter_sent: { label: "Letter Sent", color: "bg-blue-100 text-blue-700", icon: Send },
    awaiting_response: { label: "Awaiting Response", color: "bg-yellow-100 text-yellow-700", icon: Clock },
    response_received: { label: "Response Received", color: "bg-purple-100 text-purple-700", icon: MessageSquare },
    resolved: { label: "Resolved", color: "bg-green-100 text-green-700", icon: CheckCircle2 },
    partially_resolved: { label: "Partially Resolved", color: "bg-lime-100 text-lime-700", icon: CheckCircle2 },
    denied: { label: "Denied", color: "bg-red-100 text-red-700", icon: XCircle },
    escalated: { label: "Escalated to CFPB", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
    litigation: { label: "Legal Action", color: "bg-red-100 text-red-800", icon: Scale }
};

const OUTCOMES = {
    positive: { label: "Positive - Item Removed/Corrected", color: "text-green-600" },
    negative: { label: "Negative - No Change", color: "text-red-600" },
    partial: { label: "Partial - Some Correction Made", color: "text-yellow-600" },
    pending: { label: "Pending - Awaiting Final Decision", color: "text-gray-600" }
};

const DisputeTrackerPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const [disputes, setDisputes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [initializing, setInitializing] = useState(false);
    const [selectedDispute, setSelectedDispute] = useState(null);
    const [updateModalOpen, setUpdateModalOpen] = useState(false);
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [updating, setUpdating] = useState(false);
    const [checkoutLoading, setCheckoutLoading] = useState(false);
    const [pricing, setPricing] = useState({ price: null, formatted: '…', monthly_formatted: '…' });
    const [pricingOpen, setPricingOpen] = useState(false);
    const [cfpbModalOpen, setCfpbModalOpen] = useState(false);
    const [cfpbContent, setCfpbContent] = useState("");
    const [cfpbLoading, setCfpbLoading] = useState(false);
    
    const [updateForm, setUpdateForm] = useState({
        status: "",
        outcome: "",
        notes: "",
        response_details: ""
    });

    const loadPricing = useCallback(async () => {
        try {
            const response = await paymentsAPI.getPricing();
            setPricing(response.data);
        } catch (error) {
            // Non-critical — defaults already set in state
        }
    }, []);

    const loadDisputes = useCallback(async () => {
        try {
            const response = await disputesAPI.getAll();
            setDisputes(response.data);
        } catch (error) {
            toast.error(error.response?.data?.detail || "Could not load disputes");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadDisputes();
        loadPricing();
    }, [loadDisputes, loadPricing]);

    const handleInitialize = async () => {
        setInitializing(true);
        try {
            const response = await disputesAPI.initialize();
            setDisputes(response.data);
            toast.success("Dispute tracking initialized!");
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to initialize tracking");
        } finally {
            setInitializing(false);
        }
    };

    const handleUpgrade = () => {
        setPricingOpen(true);
    };

    const openUpdateModal = (dispute) => {
        setSelectedDispute(dispute);
        setUpdateForm({
            status: dispute.status,
            outcome: dispute.outcome || "",
            notes: dispute.notes || "",
            response_details: dispute.response_details || ""
        });
        setUpdateModalOpen(true);
    };

    const openHistoryModal = (dispute) => {
        setSelectedDispute(dispute);
        setHistoryModalOpen(true);
    };

    const openCfpbModal = async (dispute) => {
        setSelectedDispute(dispute);
        setCfpbContent("");
        setCfpbModalOpen(true);
        setCfpbLoading(true);
        try {
            const res = await disputesAPI.generateCfpbComplaint(dispute.id);
            setCfpbContent(res.data?.complaint || "");
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not generate CFPB complaint");
            setCfpbModalOpen(false);
        } finally {
            setCfpbLoading(false);
        }
    };

    const copyCfpb = () => {
        if (!cfpbContent) return;
        navigator.clipboard.writeText(cfpbContent);
        toast.success("Complaint copied to clipboard");
    };

    const downloadCfpb = () => {
        if (!cfpbContent) return;
        const blob = new Blob([cfpbContent], { type: "text/plain;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `cfpb_complaint_${(selectedDispute?.account_name || "dispute").replace(/\s+/g, "_").toLowerCase()}.txt`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
    };

    const handleUpdateDispute = async () => {
        if (!selectedDispute) return;
        
        setUpdating(true);
        try {
            const response = await disputesAPI.update(selectedDispute.id, updateForm);
            
            // Update local state
            setDisputes(prev => prev.map(d => 
                d.id === selectedDispute.id ? response.data : d
            ));
            
            setUpdateModalOpen(false);
            toast.success("Dispute updated! Check your new recommended next steps.");
            
            // Show AI recommendation
            if (response.data.ai_recommendation) {
                setSelectedDispute(response.data);
            }
        } catch (error) {
            toast.error(error.response?.data?.detail || "Failed to update dispute");
        } finally {
            setUpdating(false);
        }
    };

    const getStatusIcon = (status) => {
        const StatusIcon = STATUSES[status]?.icon || Clock;
        return <StatusIcon className="h-4 w-4" />;
    };

    const getProgress = () => {
        if (disputes.length === 0) return 0;
        const resolved = disputes.filter(d => 
            d.status === 'resolved' || d.status === 'partially_resolved'
        ).length;
        return Math.round((resolved / disputes.length) * 100);
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-background">
                <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
            </div>
        );
    }

    // Show upgrade prompt if not paid
    if (!user?.has_paid) {
        return (
            <div className="min-h-screen bg-background">
                <nav className="nav-glass sticky top-0 z-50">
                    <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                        <Link to="/" className="flex items-center gap-2">
                            <Shield className="h-8 w-8 text-primary" />
                            <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                        </Link>
                    </div>
                </nav>
                
                <main className="max-w-2xl mx-auto px-4 md:px-8 py-16 text-center">
                    <Lock className="h-16 w-16 text-muted-foreground mx-auto mb-6" />
                    <h1 className="text-3xl font-serif font-bold text-foreground mb-4">
                        Unlock Dispute Tracking
                    </h1>
                    <p className="text-muted-foreground mb-8">
                        Track your disputes, update their status, and get AI-powered next step recommendations 
                        as your credit repair journey progresses.
                    </p>
                    <Button 
                        size="lg"
                        className="rounded-full btn-press"
                        onClick={handleUpgrade}
                        disabled={checkoutLoading}
                        data-testid="upgrade-btn"
                    >
                        <CreditCard className="h-5 w-5 mr-2" />
                        See Plans · from {pricing.monthly_formatted || '…'}/mo
                    </Button>
                    <Link to="/dashboard" className="block mt-4">
                        <Button variant="ghost">Back to Dashboard</Button>
                    </Link>
                </main>
                <PricingModal open={pricingOpen} onOpenChange={setPricingOpen} defaultPlan="monthly" />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="nav-glass sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        <Shield className="h-8 w-8 text-primary" />
                        <span className="font-serif text-xl font-semibold">CreditFix AI</span>
                    </Link>
                    <Badge className="badge-premium">Full Access</Badge>
                </div>
            </nav>

            <main className="max-w-7xl mx-auto px-4 md:px-8 py-8">
                {/* Back Button */}
                <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
                    <ArrowLeft className="h-4 w-4" />
                    Back to Dashboard
                </Link>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-8"
                >
                    <h1 className="text-3xl font-serif font-bold text-foreground mb-2">
                        Dispute Tracker
                    </h1>
                    <p className="text-muted-foreground">
                        Track your disputes, update status, and get AI-powered guidance for next steps.
                    </p>
                </motion.div>

                {disputes.length === 0 ? (
                    // Initialize disputes
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="max-w-lg mx-auto text-center py-12"
                    >
                        <RefreshCw className="h-16 w-16 text-primary mx-auto mb-6" />
                        <h2 className="text-xl font-serif font-semibold mb-4">
                            Initialize Dispute Tracking
                        </h2>
                        <p className="text-muted-foreground mb-8">
                            Create trackers for each issue found in your credit analysis. 
                            You'll be able to update status and get personalized next steps.
                        </p>
                        <Button 
                            size="lg"
                            className="rounded-full btn-press"
                            onClick={handleInitialize}
                            disabled={initializing}
                            data-testid="initialize-btn"
                        >
                            {initializing ? (
                                <span className="flex items-center gap-2">
                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                    Initializing...
                                </span>
                            ) : (
                                <>
                                    <Sparkles className="h-5 w-5 mr-2" />
                                    Start Tracking Disputes
                                </>
                            )}
                        </Button>
                    </motion.div>
                ) : (
                    <>
                        {/* Progress Overview */}
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1 }}
                            className="mb-8"
                        >
                            <Card className="border-border">
                                <CardContent className="pt-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div>
                                            <p className="text-sm text-muted-foreground">Overall Progress</p>
                                            <p className="text-2xl font-bold font-mono">{getProgress()}% Complete</p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-sm text-muted-foreground">Disputes</p>
                                            <p className="text-2xl font-bold font-mono">{disputes.length}</p>
                                        </div>
                                    </div>
                                    <div className="w-full bg-muted rounded-full h-3">
                                        <div 
                                            className="bg-accent h-3 rounded-full transition-all duration-500"
                                            style={{ width: `${getProgress()}%` }}
                                        />
                                    </div>
                                    <div className="flex gap-4 mt-4 text-sm">
                                        <span className="flex items-center gap-1">
                                            <span className="w-3 h-3 rounded-full bg-green-500" />
                                            Resolved: {disputes.filter(d => d.status === 'resolved' || d.status === 'partially_resolved').length}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <span className="w-3 h-3 rounded-full bg-yellow-500" />
                                            In Progress: {disputes.filter(d => ['letter_sent', 'awaiting_response', 'response_received'].includes(d.status)).length}
                                        </span>
                                        <span className="flex items-center gap-1">
                                            <span className="w-3 h-3 rounded-full bg-gray-400" />
                                            Pending: {disputes.filter(d => d.status === 'pending').length}
                                        </span>
                                    </div>
                                </CardContent>
                            </Card>
                        </motion.div>

                        {/* Disputes List */}
                        <div className="space-y-4">
                            <AnimatePresence>
                                {disputes.map((dispute, index) => (
                                    <motion.div
                                        key={dispute.id}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: 0.1 * index }}
                                    >
                                        <Card className="border-border card-hover" data-testid={`dispute-${index}`}>
                                            <CardContent className="pt-6">
                                                <div className="flex flex-col lg:flex-row lg:items-start gap-6">
                                                    {/* Dispute Info */}
                                                    <div className="flex-1 space-y-3">
                                                        <div className="flex items-start justify-between">
                                                            <div>
                                                                <h3 className="font-semibold text-foreground">
                                                                    {dispute.issue_type}
                                                                </h3>
                                                                <p className="text-sm text-muted-foreground">
                                                                    {dispute.account_name} • {dispute.recipient}
                                                                </p>
                                                            </div>
                                                            <Badge className={STATUSES[dispute.status]?.color || "bg-gray-100"}>
                                                                {getStatusIcon(dispute.status)}
                                                                <span className="ml-1">{STATUSES[dispute.status]?.label}</span>
                                                            </Badge>
                                                        </div>
                                                        
                                                        <p className="text-sm text-muted-foreground">
                                                            {dispute.description}
                                                        </p>

                                                        {dispute.outcome && (
                                                            <p className={`text-sm font-medium ${OUTCOMES[dispute.outcome]?.color}`}>
                                                                Outcome: {OUTCOMES[dispute.outcome]?.label}
                                                            </p>
                                                        )}
                                                    </div>

                                                    {/* Next Steps */}
                                                    <div className="lg:w-80 space-y-3">
                                                        <p className="text-sm font-medium text-foreground">Next Steps:</p>
                                                        <div className="space-y-2">
                                                            {dispute.next_steps?.slice(0, 2).map((step, i) => (
                                                                <div key={i} className="flex items-start gap-2 text-sm">
                                                                    <span className="w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 text-xs font-medium">
                                                                        {step.step}
                                                                    </span>
                                                                    <span className="text-muted-foreground">{step.action}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>

                                                    {/* Actions */}
                                                    <div className="flex lg:flex-col gap-2">
                                                        <Button 
                                                            onClick={() => openUpdateModal(dispute)}
                                                            className="flex-1 lg:flex-none rounded-full"
                                                            data-testid={`update-dispute-${index}`}
                                                        >
                                                            Update Status
                                                            <ChevronRight className="h-4 w-4 ml-1" />
                                                        </Button>
                                                        <Button 
                                                            variant="outline"
                                                            onClick={() => openHistoryModal(dispute)}
                                                            className="flex-1 lg:flex-none rounded-full"
                                                            data-testid={`history-${index}`}
                                                        >
                                                            <History className="h-4 w-4 mr-1" />
                                                            History
                                                        </Button>
                                                        <Button 
                                                            variant="outline"
                                                            onClick={() => openCfpbModal(dispute)}
                                                            className="flex-1 lg:flex-none rounded-full border-orange-300 text-orange-700 hover:bg-orange-50"
                                                            data-testid={`cfpb-${index}`}
                                                        >
                                                            <Flag className="h-4 w-4 mr-1" />
                                                            CFPB
                                                        </Button>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    </>
                )}
            </main>

            {/* Update Status Modal */}
            <Dialog open={updateModalOpen} onOpenChange={setUpdateModalOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif">Update Dispute Status</DialogTitle>
                        <DialogDescription>
                            {selectedDispute?.issue_type} - {selectedDispute?.account_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Status</Label>
                            <Select 
                                value={updateForm.status} 
                                onValueChange={(value) => setUpdateForm({...updateForm, status: value})}
                            >
                                <SelectTrigger data-testid="status-select">
                                    <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                    {Object.entries(STATUSES).map(([key, { label }]) => (
                                        <SelectItem key={key} value={key}>{label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {['response_received', 'resolved', 'partially_resolved', 'denied'].includes(updateForm.status) && (
                            <div className="space-y-2">
                                <Label>Outcome</Label>
                                <Select 
                                    value={updateForm.outcome} 
                                    onValueChange={(value) => setUpdateForm({...updateForm, outcome: value})}
                                >
                                    <SelectTrigger data-testid="outcome-select">
                                        <SelectValue placeholder="Select outcome" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {Object.entries(OUTCOMES).map(([key, { label }]) => (
                                            <SelectItem key={key} value={key}>{label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        {updateForm.status === 'response_received' && (
                            <div className="space-y-2">
                                <Label>Response Details</Label>
                                <Textarea 
                                    placeholder="What did the bureau/creditor say in their response?"
                                    value={updateForm.response_details}
                                    onChange={(e) => setUpdateForm({...updateForm, response_details: e.target.value})}
                                    rows={3}
                                    data-testid="response-details-input"
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Notes (Optional)</Label>
                            <Textarea 
                                placeholder="Any additional notes about this update..."
                                value={updateForm.notes}
                                onChange={(e) => setUpdateForm({...updateForm, notes: e.target.value})}
                                rows={2}
                                data-testid="notes-input"
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUpdateModalOpen(false)}>
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleUpdateDispute}
                            disabled={updating || !updateForm.status}
                            className="rounded-full"
                            data-testid="submit-update-btn"
                        >
                            {updating ? (
                                <span className="flex items-center gap-2">
                                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                    Getting AI Recommendations...
                                </span>
                            ) : (
                                <>
                                    <Sparkles className="h-4 w-4 mr-2" />
                                    Update & Get Next Steps
                                </>
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* History Modal */}
            <Dialog open={historyModalOpen} onOpenChange={setHistoryModalOpen}>
                <DialogContent className="sm:max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-serif">Dispute History</DialogTitle>
                        <DialogDescription>
                            {selectedDispute?.issue_type} - {selectedDispute?.account_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <ScrollArea className="h-[400px] pr-4">
                        <div className="relative">
                            <div className="absolute left-[11px] top-2 bottom-2 w-0.5 bg-border" />
                            <div className="space-y-5">
                                {selectedDispute?.history?.map((entry, i) => {
                                    const statusInfo = STATUSES[entry.status] || { color: "bg-gray-100 text-gray-700", icon: Clock };
                                    const Icon = statusInfo.icon;
                                    return (
                                        <div key={i} className="relative pl-9">
                                            <div className={`absolute left-0 top-0 h-6 w-6 rounded-full flex items-center justify-center border-2 border-background shadow-sm ${statusInfo.color}`}>
                                                <Icon className="h-3 w-3" />
                                            </div>
                                            <div>
                                                <p className="text-xs text-muted-foreground">
                                                    {new Date(entry.date).toLocaleDateString()} · {new Date(entry.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                </p>
                                                <p className="text-sm font-medium text-foreground mt-0.5">
                                                    {entry.action}
                                                </p>
                                                {entry.outcome && OUTCOMES[entry.outcome] && (
                                                    <p className={`text-xs mt-0.5 ${OUTCOMES[entry.outcome].color}`}>
                                                        {OUTCOMES[entry.outcome].label}
                                                    </p>
                                                )}
                                                {entry.notes && (
                                                    <p className="text-sm text-muted-foreground mt-1 italic">
                                                        "{entry.notes}"
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </ScrollArea>

                    <DialogFooter>
                        <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* CFPB Complaint Modal */}
            <Dialog open={cfpbModalOpen} onOpenChange={setCfpbModalOpen}>
                <DialogContent className="sm:max-w-2xl" data-testid="cfpb-modal">
                    <DialogHeader>
                        <DialogTitle className="font-serif flex items-center gap-2">
                            <Flag className="h-5 w-5 text-orange-600" />
                            CFPB Complaint Draft
                        </DialogTitle>
                        <DialogDescription>
                            AI-drafted, ready to paste into <a href="https://www.consumerfinance.gov/complaint/" target="_blank" rel="noreferrer" className="underline">consumerfinance.gov/complaint</a>.
                            Personalize before submitting.
                        </DialogDescription>
                    </DialogHeader>

                    {cfpbLoading ? (
                        <div className="flex flex-col items-center justify-center h-[400px] gap-3">
                            <span className="animate-spin rounded-full h-10 w-10 border-2 border-primary border-t-transparent" />
                            <p className="text-sm text-muted-foreground">Drafting your complaint with GPT-5.2...</p>
                        </div>
                    ) : (
                        <ScrollArea className="h-[400px] rounded-lg border border-border bg-muted/30 p-4">
                            <pre className="text-sm font-mono whitespace-pre-wrap text-foreground" data-testid="cfpb-text">
                                {cfpbContent}
                            </pre>
                        </ScrollArea>
                    )}

                    <DialogFooter className="gap-2">
                        <Button variant="outline" onClick={copyCfpb} disabled={cfpbLoading || !cfpbContent} data-testid="cfpb-copy-btn">
                            <Copy className="h-4 w-4 mr-2" />
                            Copy
                        </Button>
                        <Button variant="outline" onClick={downloadCfpb} disabled={cfpbLoading || !cfpbContent} data-testid="cfpb-download-btn">
                            <Download className="h-4 w-4 mr-2" />
                            Download .txt
                        </Button>
                        <Button onClick={() => setCfpbModalOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default DisputeTrackerPage;
